/**
 * Apple Pay integration composable managing the complete payment session lifecycle.
 *
 * Orchestrates Apple Pay button initialization, merchant validation, address/shipping method
 * selection, payment authorization, and order placement. Supports both standard checkout flow
 * and PDP express checkout (Buy Now), including BOPIS (Buy Online, Pick Up In Store) collection points.
 *
 * @param storeContext - Optional cart context identifier; use `'applePayPdp'` for PDP express checkout (temporary cart), omit for standard checkout flow
 *
 * @returns Object containing Apple Pay session management
 *
 * @example
 * ```ts
 * // Standard checkout (cart page)
 * const { initApplePaySession, isApplePayActive } = useApplePay()
 * initApplePaySession()
 *
 * // PDP express checkout with Buy Now
 * const { initApplePaySession, isApplePayActive } = useApplePay('applePayPdp')
 * initApplePaySession({
 *   productId: 'abc123',
 *   upc: '123456789',
 *   storeId: 'store-001' // optional, for BOPIS
 * })
 *
 * // Check if session is active (for loading states)
 * <button :disabled="isApplePayActive">Apple Pay</button>
 * ```
 *
 * @remarks
 * **Browser-only**: Requires `window.ApplePaySession` - not available during SSR. Always check
 * `ApplePaySession.canMakePayments()` before rendering the Apple Pay button.
 *
 * **Session lifecycle events** (handled internally):
 * 1. `onvalidatemerchant` - Validates merchant session with backend
 * 2. `onpaymentmethodselected` - User selects payment card/method
 * 3. `onshippingcontactselected` - User provides/changes shipping address
 * 4. `onshippingmethodselected` - User selects shipping method (Standard, Express, etc.)
 * 5. `onpaymentauthorized` - User authorizes payment → order placement
 * 6. `oncancel` - User cancels Apple Pay sheet
 *
 * **Multi-brand behavior:**
 * - Merchant capabilities, supported networks, and required contact fields configured per brand in `app.config.ts`
 * - All brands use Apple Pay v3+ features (when available): storePickup, addressUnserviceable errors
 *
 * **PDP Express Checkout (`storeContext = 'applePayPdp'`):**
 * - Creates temporary Pinia stores (`cart${storeContext}`, `checkout${storeContext}`)
 * - Adds product to temp cart during `onvalidatemerchant`
 * - Cleans up temp stores on success/failure/cancel
 * - Uses `?bid=cookie` query param for cart API calls
 *
 * **BOPIS (Collection Points):**
 * - Detected via `cart.hasCollectionPointItems && !isCartPage`
 * - Sets `shippingType: 'storePickup'` and `shippingContactEditingMode: 'storePickup'`
 * - Pulls collection point details from `sessionStorage` (`deliveryOptions.collectionPoint`)
 * - Sends `storeId`, `storeName`, `optionType`, `bookingCode` to `addShippingAddress` API
 *
 * **Address validation:**
 * - Mandatory fields: `givenName`, `familyName`, `addressLines[0]`, `postalCode`
 * - Rejects non-Latin, non-Greek characters (allows basic Latin + EMEA diacritics: àáâãäåæçèéêë...,
 *   and Greek: Αα-Ωω with tonos/dialytika, e.g. ά, ϊ, ΰ - GLOBAL15-133090; still excludes Cyrillic, CJK)
 * - Country code must match current locale (`useCountryCode()`)
 * - City name max length: 50 characters (SFCC downstream constraint)
 *
 * **Feature flags:**
 * - `allowApplePayForAll` - Enables deep copy workaround for Apple Pay object serialization
 * - `enableShippingAddressRecaptcha` - Adds reCAPTCHA to shipping address submission
 *
 * **Auto-promo codes:**
 * - Applied during `onvalidatemerchant` if `isAutoPromoCodeEnabled` feature flag is on
 * - Silent failure (logs error, doesn't interrupt session) to avoid blocking sale
 *
 * **Error handling:**
 * - All errors result in `ApplePayError` sent to Apple Pay sheet
 * - Temporary stores cleaned up on any failure
 * - Common error codes: `'unknown'`, `'shippingContactInvalid'`, `'billingContactInvalid'`, `'addressUnserviceable'`
 *
 * **Security:**
 * - Payment token from Apple Pay passed to `placeOrder` as `c_applePayToken` (stringified)
 * - reCAPTCHA v3 executed on `placeOrder` (action: `'placeOrder'`)
 * - Session ID generated via `uuid()` for payment instrument tracking
 *
 * **Navigation after success:**
 * - Standard checkout: `navigateTo('/order-confirmation')`
 * - PDP express: `navigateTo('/order-confirmation?storeContext=applePayPdp')`
 *
 * @see domains/commerce/composables/useApplePay/README.md
 * @see https://developer.apple.com/documentation/apple_pay_on_the_web
 */
import type { ApplePayInitPayload, ApplePayShippingMethod, CartStoreAddress } from '#types/applePay'
import type { CartState, CartStoreContext, DeliveryOptionsStorage, InvoiceData } from '#types/cart'
import type { Order } from '#types/components/checkout/order'

declare global {
  interface Window {
    ApplePaySession: ApplePaySession
    ApplePayError: ApplePayError
  }
}

export const useApplePay = (storeContext?: CartStoreContext) => {
  const { $t } = useNuxtApp()
  const {
    checkout: { applePay },
    pages: { cart: { coupon: { isAutoPromoCodeEnabled } } }
  } = useAppConfig()
  const api = useApi()
  const cart = useCartStore(storeContext)
  const checkout = useCheckoutStore(storeContext)
  const route = useRoute()
  const { allowApplePayForAll, enableShippingAddressRecaptcha } = useFeatureFlags()
  const { load, countries } = useAddressApiRules()

  const deliveryOptions = useSessionStorage('deliveryOptions', { collectionPoint: {}, shipToHome: {} } as DeliveryOptionsStorage)
  const selectedCollectionPoint = computed(() => deliveryOptions.value.collectionPoint)

  const paymentRequestRef = ref<ApplePayJS.ApplePayPaymentRequest>()
  // used to set loading state on Apple Pay button
  const isApplePayActive = ref(false)

  // for temporary cart we need to pass a query parameter to some endpoints. More details here domains/commerce/composables/useApplePay/README.md
  const queryParams = computed(() => storeContext === 'applePayPdp' ? { bid: 'cookie' } : undefined)

  // we need to check if user is on cart page so we don't use the collectionPoint flow if they started it on checkout and went back to cart
  const isCartPage = computed(() => route.name?.toString().includes('cart'))
  // we can't use the collectionPoint flow if user is on cart (in case they've started collection point flow on checkout and went back)
  const isCollectionPoint = computed(() => cart.hasCollectionPointItems && !isCartPage.value)

  /**
   * Builds Apple Pay line items array from current cart totals.
   *
   * Includes subtotal, shipping, applied gift cards (with masked card numbers), discounts, and taxes
   * (taxes shown only if `cart.totals.taxation === 'net'` - SFCC returns 'net' for tax-inclusive regions).
   *
   * @returns Array of Apple Pay line items ready for payment request
   *
   * @example
   * ```ts
   * const lineItems = getLineItems()
   * // Returns:
   * // [
   * //   { label: 'Subtotal', amount: '99.99', type: 'final' },
   * //   { label: 'Shipping', amount: '5.99' },
   * //   { label: 'Gift Card - ****1234', amount: '-25.00' },
   * //   { label: 'Discount', amount: '-10.00' },
   * //   { label: 'Tax', amount: '7.50' } // only if taxation === 'net'
   * // ]
   * ```
   *
   * @remarks
   * Gift card amounts are negative and displayed with masked card numbers (via `maskGiftCard` util).
   * Discount amounts are positive in SFCC response but should be negative for Apple Pay display.
   */
  const getLineItems = (): ApplePayJS.ApplePayLineItem[] => {
    if (!cart.totals)
      return []

    const lineItems: ApplePayJS.ApplePayLineItem[] = [
      {
        label: $t.subtotal,
        amount: cart.totals.itemTotal.toString(),
        type: 'final'
      },
      {
        label: $t.shipping,
        amount: cart.totals.shipping.toString()
      },
      ...cart.giftCards.map((giftCard) => ({
        label: giftCard.c_gcCardNumber ? `${$t.giftCard} - ${maskGiftCard(giftCard.c_gcCardNumber)}` : '',
        amount: `-${giftCard.amount}`
      }))
    ]

    if (cart.totals.totalDiscount) {
      lineItems.push({
        label: $t.discountItem,
        amount: `${cart.totals.totalDiscount}`
      })
    }
    // we have to show taxes on the FE only if the taxation comes back as 'net' from the BE
    if (cart.totals.taxation === 'net') {
      lineItems.push({
        label: $t.taxLabel,
        amount: cart.totals.tax.toString()
      })
    }

    return lineItems
  }

  /**
   * Builds the total amount line item for Apple Pay payment request.
   *
   * Uses `cart.totals.remainingToPay` if available (when gift cards are applied), otherwise uses
   * `cart.totals.total`. Shown as the primary total at the top of the Apple Pay sheet.
   *
   * @returns Apple Pay line item representing the final total amount to charge
   *
   * @example
   * ```ts
   * const total = getPaymentTotal()
   * // With gift card applied:
   * // { label: 'Total', amount: '74.99', type: 'final' } // remainingToPay
   * // Without gift card:
   * // { label: 'Total', amount: '99.99', type: 'final' } // total
   * ```
   */
  const getPaymentTotal = (): ApplePayJS.ApplePayLineItem => {
    if (!cart.totals) {
      return {
        label: '',
        amount: '',
        type: 'final'
      }
    }

    const cartPaymentTotal = cart.totals.remainingToPay !== undefined
      ? cart.totals.remainingToPay
      : cart.totals.total

    return {
      label: $t.total,
      amount: String(cartPaymentTotal),
      type: 'final'
    }
  }

  /**
   * Maps cart shipping methods to Apple Pay shipping method format.
   *
   * Filters out BOPIS/PUDO methods (`pickup`, `pudo`, `sts`) and returns only ship-to-home methods.
   * Marks the currently selected method with `selected: true`. Returns empty array for collection point flow.
   *
   * @returns Array of Apple Pay shipping methods with labels, amounts, delivery estimates, and selection state
   *
   * @example
   * ```ts
   * const methods = getApplePayShippingMethods()
   * // [
   * //   { label: 'Standard', amount: '5.99', detail: 'Estimated delivery: Feb 10-12', identifier: 'standard', selected: true },
   * //   { label: 'Express', amount: '12.99', detail: 'Estimated delivery: Feb 8', identifier: 'express', selected: false }
   * // ]
   * ```
   *
   * @remarks
   * - Collection point flow returns `[]` (no shipping method selection needed)
   * - Delivery time formatted via `useFormattedDeliveryDate` composable
   * - Filters out `pickup`, `pudo`, `sts` codes per GLOBAL15-77020
   */
  const getApplePayShippingMethods = () => {
    // no need to add applicable shipping methods for collection points as there is no shipping method selection for this flow
    if (isCollectionPoint.value || !cart.shippingMethods[0]) return []
    const mapShippingMethod = ({ label, price: { finalAmount, amount }, code, deliveryTime = '' }) => ({
      label,
      amount: finalAmount ?? amount,
      detail: useFormattedDeliveryDate($t.estimatedDelivery, deliveryTime),
      identifier: code,
      selected: cart.shippingMethods[0].code === code
    })
    if (cart.shippingMethods[0].applicableShippingMethods.length)
      // don't show pickup or pudo methods even though they are coming from the services https://digital.vfc.com/jira/browse/GLOBAL15-77020
      return cart.shippingMethods[0].applicableShippingMethods.filter((method) => method.code !== 'pickup' && method.code !== 'pudo' && method.code !== 'sts').map(mapShippingMethod)

    return cart.shippingMethods.reduce((allMethods, group) => (group.shippingId === 'me'
      ? allMethods.concat(group.applicableShippingMethods.map(mapShippingMethod))
      : []
    ), <ApplePayShippingMethod[]>[])
  }

  /**
   * Maps Apple Pay payment contact object to Canvas cart address format.
   *
   * Transforms Apple Pay's address structure (e.g., `givenName`, `familyName`, `addressLines[]`, `administrativeArea`)
   * to Canvas's cart API schema (e.g., `firstName`, `lastName`, `addressLine1`, `province`).
   *
   * @param address - Apple Pay payment contact object (shipping or billing contact from session event)
   * @returns Array containing single address object in Canvas cart format (always single-element array for API compatibility)
   *
   * @example
   * ```ts
   * const appleAddress = event.payment.shippingContact
   * const cartAddress = mapAddressData(appleAddress)
   * // [
   * //   {
   * //     firstName: 'John',
   * //     lastName: 'Doe',
   * //     email: 'john.doe@example.com',
   * //     country: 'US',
   * //     addressLine1: '123 Main St',
   * //     addressLine2: 'Apt 4B',
   * //     postalCode: '10001',
   * //     city: 'New York',
   * //     province: 'NY',
   * //     phone: '+12125551234',
   * //     stateCode: 'NY',
   * //     countryCode: 'US'
   * //   }
   * // ]
   * ```
   *
   * @remarks
   * - Postal code and province are uppercased for consistency with backend expectations
   * - Returns array (not single object) to match cart API `$addShippingAddress` endpoint signature
   * - All fields default to empty strings if not provided by Apple Pay
   */
  const mapAddressData = (address: ApplePayJS.ApplePayPaymentContact = {}) => ([
    {
      addressId: '',
      firstName: address.givenName || '',
      lastName: address.familyName || '',
      email: address.emailAddress || '',
      country: address.countryCode || '',
      addressLine1: address.addressLines?.[0] || '',
      addressLine2: address.addressLines?.[1] || '',
      postalCode: address.postalCode?.toUpperCase() || '',
      city: address.locality || '',
      province: address.administrativeArea?.toUpperCase() || '',
      phone: address.phoneNumber || '',
      stateCode: address.administrativeArea?.toUpperCase() || '',
      countryCode: address.countryCode?.toUpperCase() || ''
    }
  ])

  /**
   * Maps Apple Pay contact field names to error-compatible field names.
   *
   * Apple Pay error constructor requires specific field names (e.g., `'name'` instead of `'givenName'`/`'familyName'`).
   * This function normalizes contact field keys to valid `ApplePayErrorContactField` values.
   *
   * @param field - Contact field key from `ApplePayPaymentContact` interface
   * @returns Normalized field name compatible with `ApplePayError` constructor
   *
   * @example
   * ```ts
   * mapAddressField('givenName') // 'name'
   * mapAddressField('familyName') // 'name'
   * mapAddressField('addressLines') // 'addressLines' (unchanged)
   * ```
   *
   * @remarks
   * Used in `isAddressValid` to construct `ApplePayError` objects with correct contact field references.
   */
  const mapAddressField = (field: keyof ApplePayJS.ApplePayPaymentContact): ApplePayJS.ApplePayErrorContactField => {
    switch (field) {
      case 'familyName':
      case 'givenName':
      case 'phoneticFamilyName':
      case 'phoneticGivenName':
        return 'name'
    }

    return field
  }

  /**
   * Validates Apple Pay shipping/billing address against Canvas address requirements.
   *
   * Checks:
   * 1. Mandatory fields exist and are not whitespace-only: `givenName`, `familyName`, `addressLines[0]`, `postalCode`
   * 2. No non-Latin/non-Greek characters (allows basic Latin + EMEA diacritics: àáâãäåæçèéêë...,
   *    and Greek script U+0370-U+03FF/U+1F00-U+1FFF - GLOBAL15-133090; excludes Cyrillic, CJK)
   *
   * @param address - Apple Pay payment contact object (shipping or billing)
   * @returns Tuple: `[isValid, invalidFieldName]` - if valid, `invalidFieldName` is `undefined`
   *
   * @example
   * ```ts
   * const [isValid, errorField] = isAddressValid(event.payment.billingContact)
   * if (!isValid) {
   *   session.completePayment({
   *     status: ApplePaySession.STATUS_FAILURE,
   *     errors: [new ApplePayError('billingContactInvalid', errorField)]
   *   })
   * }
   * ```
   *
   * @remarks
   * - Phone and email fields are exempt from character validation (international phone formats, email punycode)
   * - Allowed characters regex: `/^[\wßàáâãäåæçèéêëìíîïðñòóôõöøùúûüýÿąćčďėęěįłńňœřšťūůųźżž∂\u0370-\u03FF\u1F00-\u1FFF\s,.'`\-+@&#*():;<>/\][]+$/i`
   *   (basic Latin + EMEA diacritics + Greek U+0370-U+03FF/U+1F00-U+1FFF - GLOBAL15-133090)
   * - Even if Apple Pay requires fields in config, incorrect system settings can bypass validation → we validate server-side too
   * - This allow-list is intentionally global (not keyed by `useCountryCode()`/`useLocale()`): Apple Pay can present a
   *   user's saved Apple ID contact in any script regardless of the active storefront locale, and the session callbacks
   *   must resolve synchronously, so it does not key off the async, per-country `useAddressApiRules()` patterns used by
   *   the standard address form - see composable-level @remarks "Address validation" above for why
   */
  const isAddressValid
    = (address?: ApplePayJS.ApplePayPaymentContact): [boolean, ApplePayJS.ApplePayErrorContactField | undefined] => {
      if (!address) return [false, 'addressLines']

      let errorField: keyof ApplePayJS.ApplePayPaymentContact | undefined
      const mandatoryFields: (keyof ApplePayJS.ApplePayPaymentContact)[]
        = ['addressLines', 'postalCode', 'givenName', 'familyName']

      const areMandatoryFieldsValid = mandatoryFields.every((fieldName) => {
        const fieldValue = address[fieldName]
        // regardless of Apple Pay configuration, incorrect data can be set in system settings
        // and if user does not interact with the form, it will be submitted regardless of rules
        // so mandatory fields might not exist in the payload
        if (!fieldValue) {
          errorField = fieldName as keyof ApplePayJS.ApplePayPaymentContact
          return false
        }

        // validate if fields don't contain only whitespaces
        if (isString(fieldValue) && !fieldValue.trim()) {
          errorField = fieldName as keyof ApplePayJS.ApplePayPaymentContact
          return false
        }

        // addressLines are stored in an array and only Line1 is mandatory
        if (isArray(fieldValue) && fieldName === 'addressLines' && !fieldValue[0].trim()) {
          errorField = 'addressLines'
          return false
        }

        return true
      })

      // ensure that we use only allowed characters which are basic latin + the variations used across an EMEA region
      // + Greek script (U+0370-U+03FF basic Greek incl. tonos/dialytika, U+1F00-U+1FFF Greek Extended/polytonic)
      // GLOBAL15-133090: Greek-alphabet addresses were being rejected for VANS/TNF GR
      const doesContainNonLatinCharacters = Object.entries(address)
        .filter(([fieldName]) => !['phoneNumber', 'emailAddress'].includes(fieldName))
        .some(([fieldName, fieldValue]) => {
          const allowedCharactersRegexp = /^[\wßàáâãäåæçèéêëìíîïðñòóôõöøùúûüýÿąćčďėęěįłńňœřšťūůųźżž∂\u0370-\u03FF\u1F00-\u1FFF\s,.'`\-+@&#*():;<>/\][]+$/i
          if (isArray(fieldValue)) {
            const isInvalid = fieldValue.some((value) =>
              isString(value) && !!value && !value.match(allowedCharactersRegexp))
            if (isInvalid) errorField = fieldName as keyof ApplePayJS.ApplePayPaymentContact
            return isInvalid
          }
          const isInvalid = isString(fieldValue) && !!fieldValue && !fieldValue.match(allowedCharactersRegexp)
          if (isInvalid) errorField = fieldName as keyof ApplePayJS.ApplePayPaymentContact
          return isInvalid
        })

      return [areMandatoryFieldsValid && !doesContainNonLatinCharacters, errorField && mapAddressField(errorField)]
    }

  /**
   * Maps Canvas cart shipping address to Apple Pay contact format.
   *
   * Pulls address from `checkout.shippingAddress` or `cart.shippingMethods[0].address`, transforms to Apple Pay
   * `ApplePayPaymentContact` structure for pre-populating the Apple Pay sheet with saved address data.
   *
   * @returns Apple Pay payment contact object with shipping address fields populated from cart
   *
   * @example
   * ```ts
   * const applePayContact = mapCartShippingAddressToApplePay()
   * // {
   * //   phoneNumber: '+12125551234',
   * //   emailAddress: 'user@example.com',
   * //   givenName: 'Jane',
   * //   familyName: 'Smith',
   * //   addressLines: ['456 Elm St', 'Suite 200'],
   * //   locality: 'Los Angeles',
   * //   postalCode: '90001',
   * //   administrativeArea: 'CA',
   * //   countryCode: 'US'
   * // }
   * ```
   *
   * @remarks
   * - Used in `refreshPaymentRequestWithCurrentCartData` to set `shippingContact` in payment request
   * - Checks both `checkout.shippingAddress` (if user is on checkout) and `cart.shippingMethods[0].address` (if returning from cart)
   * - `addressLine2` only added to `addressLines[]` if defined (not `undefined`)
   */
  const mapCartShippingAddressToApplePay = (): ApplePayJS.ApplePayPaymentContact => {
    const cartShippingAddress = checkout.shippingAddress || cart.shippingMethods[0]?.address

    const addressLines = cartShippingAddress.addressLine1 ? [cartShippingAddress.addressLine1] : []
    if (cartShippingAddress.addressLine2 !== undefined) addressLines.push(cartShippingAddress.addressLine2)

    return {
      phoneNumber: cartShippingAddress.phone || '',
      emailAddress: cartShippingAddress.email || '',
      givenName: cartShippingAddress.firstName || '',
      familyName: cartShippingAddress.lastName || '',
      addressLines,
      locality: cartShippingAddress.city || '',
      postalCode: cartShippingAddress.postalCode || '',
      administrativeArea: cartShippingAddress.province || cartShippingAddress.stateCode || cartShippingAddress.country || '',
      countryCode: cartShippingAddress.countryCode || cartShippingAddress.country || ''
    }
  }

  /**
   * Rebuilds the Apple Pay payment request with current cart data.
   *
   * Updates `paymentRequestRef` reactive ref with fresh totals, line items, shipping methods, and
   * merchant configuration. Called whenever cart state changes (shipping method selection, coupon applied, etc.).
   *
   * @param options - Optional overrides for payment request properties
   * @param options.shippingContact - Pre-populate shipping address (used for PDP express checkout)
   *
   * @example
   * ```ts
   * // Refresh with current cart state (standard flow)
   * refreshPaymentRequestWithCurrentCartData()
   *
   * // PDP express: pre-populate with payload address
   * refreshPaymentRequestWithCurrentCartData({
   *   shippingContact: payload.shippingContact
   * })
   * ```
   *
   * @remarks
   * - **BOPIS/Collection Point**: Sets `shippingType: 'storePickup'` and `shippingContactEditingMode: 'storePickup'`
   * - **Standard checkout**: Sets `shippingType: 'shipping'` and `shippingContactEditingMode: 'enabled'`
   * - `'enabled'` is deprecated but still required for older iOS devices (< iOS 15)
   * - Always includes saved `shippingContact` if available in cart/checkout stores
   * - Merchant capabilities, supported networks, required fields pulled from `appConfig.checkout.applePay`
   */
  const refreshPaymentRequestWithCurrentCartData = (
    options: {
      shippingContact?: ApplePayJS.ApplePayPaymentContact
    } = {}
  ) => {
    paymentRequestRef.value = {
      ...paymentRequestRef.value,
      countryCode: useCountryCode(),
      currencyCode: cart.currency,
      shippingContactEditingMode: isCollectionPoint.value || options.shippingContact ? 'storePickup' : 'enabled', // Even though the docs state the value "enabled" is deprecated, but 'available' is not yet supported to older devices, so we have to use the old value that still in use and works for all devices. https://developer.apple.com/documentation/apple_pay_on_the_web/applepayshippingcontacteditingmode,
      lineItems: getLineItems(), // subtotal, tax, shipping, totalDiscount
      merchantCapabilities: applePay.merchantCapabilities,
      supportedNetworks: applePay.supportedNetworks,
      requiredBillingContactFields: applePay.requiredBillingContactFields,
      requiredShippingContactFields: applePay.requiredShippingContactFields,
      total: getPaymentTotal(), // cart.totals.total
      shippingType: isCollectionPoint.value || options.shippingContact ? 'storePickup' : 'shipping',
      shippingMethods: getApplePayShippingMethods(),
      // Always include shippingContact if we have it
      shippingContact: (checkout.shippingAddress || cart.shippingMethods[0]?.address)
        ? mapCartShippingAddressToApplePay()
        : undefined,
      ...options
    }
  }

  /**
   * Cleans up temporary Pinia stores created for PDP express checkout.
   *
   * Removes `cart${storeContext}` and `checkout${storeContext}` from Pinia state when PDP express session
   * completes (success, failure, or cancellation). No-op for standard checkout flow.
   *
   * @example
   * ```ts
   * // After order placement or session failure
   * clearTemporaryStores() // removes 'cartapplePayPdp', 'checkoutapplePayPdp'
   * ```
   *
   * @remarks
   * Only runs when `storeContext === 'applePayPdp'`. Standard checkout uses persistent stores and should not be cleared mid-session.
   */
  const clearTemporaryStores = () => {
    // clear and remove only temporary pinia stores
    if (storeContext !== 'applePayPdp') return
    const pinia = usePinia()
    delete pinia.state.value[`cart${storeContext}`]
    delete pinia.state.value[`checkout${storeContext}`]
  }

  /**
   * Initializes and begins Apple Pay session with full lifecycle event handlers.
   *
   * Main entry point for Apple Pay integration. Sets up session, attaches event handlers for merchant validation,
   * address/shipping method selection, payment authorization, and cancellation. Handles PDP express checkout flow
   * (adding product to temporary cart) and standard checkout flow.
   *
   * @param payload - Optional PDP express checkout payload (product ID, UPC, store ID for BOPIS)
   * @param invoiceAddress - Optional invoice address (for B2B or tax-exempt customers requiring separate billing address)
   *
   * @returns Apple Pay session instance (allows external manipulation if needed, though rarely used)
   *
   * @example
   * ```ts
   * // Standard checkout
   * const session = initApplePaySession()
   *
   * // PDP express checkout (Buy Now)
   * const session = initApplePaySession({
   *   productId: 'shoe-abc-123',
   *   upc: '1234567890123',
   *   storeId: 'store-nyc-5th' // optional, for BOPIS
   * })
   *
   * // With custom invoice address (B2B)
   * const session = initApplePaySession(undefined, {
   *   companyName: 'Acme Corp',
   *   taxId: '12-3456789'
   * })
   * ```
   *
   * @remarks
   * **Session lifecycle (in order):**
   * 1. `onvalidatemerchant` - Fetches merchant session from backend; adds product to cart if PDP express
   * 2. `onpaymentmethodselected` - Updates shipping methods when user changes payment card
   * 3. `onshippingcontactselected` - Validates address, updates cart with new shipping address, recalculates taxes/totals
   * 4. `onshippingmethodselected` - Updates cart with selected shipping method, recalculates totals
   * 5. `onpaymentauthorized` - Validates addresses, places order, navigates to confirmation page
   * 6. `oncancel` - Cleans up temporary stores, logs cancellation event
   *
   * **PDP express flow (`storeContext = 'applePayPdp'`):**
   * - Creates temporary cart/checkout stores
   * - Adds product during `onvalidatemerchant` (after merchant validation succeeds)
   * - Applies auto-promo code if feature flag enabled (silent failure on error)
   * - Uses `?bid=cookie` query param for all cart API calls
   * - Cleans up temporary stores on success/failure/cancel
   *
   * **BOPIS flow:**
   * - Detected via `cart.hasCollectionPointItems && !isCartPage`
   * - Adds store address with `storeId`, `storeName`, `optionType`, `bookingCode` from `sessionStorage`
   * - Sends `context: 'COLLECTION'` to backend APIs
   *
   * **Address validation:**
   * - Country code must match current locale (`useCountryCode()`)
   * - City name max 50 characters (SFCC constraint)
   * - Billing/shipping addresses checked for mandatory fields and Latin-only characters
   * - Errors returned via `ApplePayError` with specific field references
   *
   * **Feature flags:**
   * - `allowApplePayForAll` - Deep-copies payment request/merchant session (Safari serialization workaround)
   * - `enableShippingAddressRecaptcha` - Adds reCAPTCHA to shipping address submission
   * - `isAutoPromoCodeEnabled` - Auto-applies promo code from `sessionStorage` during PDP express
   *
   * **Error handling:**
   * - All errors result in `ApplePaySession.STATUS_FAILURE` + `ApplePayError`
   * - Temporary stores cleaned up on any failure
   * - Common error codes: `'unknown'`, `'shippingContactInvalid'`, `'billingContactInvalid'`, `'addressUnserviceable'`
   *
   * **Navigation:**
   * - Standard: `navigateTo('/order-confirmation')`
   * - PDP express: `navigateTo('/order-confirmation?storeContext=applePayPdp')`
   *
   * **Security:**
   * - reCAPTCHA executed on `placeOrder` (action: `'placeOrder'`)
   * - Payment token passed as stringified JSON in `c_applePayToken`
   * - Session ID generated via `uuid()` for payment instrument tracking
   * - Phone numbers formatted to E.164 standard via `formatE164phone`
   *
   * @throws Does not throw - all errors handled internally and returned to Apple Pay session via completion methods
   *
   * @see https://developer.apple.com/documentation/apple_pay_on_the_web/apple_pay_on_the_web
   * @see domains/commerce/composables/useApplePay/README.md
   */
  const initApplePaySession = (payload?: ApplePayInitPayload, invoiceAddress?: InvoiceData) => {
    const recaptcha = useRecaptcha()
    isApplePayActive.value = true

    if (!countries.value.length) load(useCountryCode(), useLocale())

    refreshPaymentRequestWithCurrentCartData(
      payload?.shippingContact ? { shippingContact: payload.shippingContact } : {}
    )

    const session = new ApplePaySession(
      applePay.version,
      allowApplePayForAll ? JSON.parse(JSON.stringify(paymentRequestRef.value)) : paymentRequestRef.value!
    )

    const invoiceAddressData = invoiceAddress || {}

    // Payment Option change callback
    session.onpaymentmethodselected = async () => {
      try {
        await cart.getApplicableShippingMethods()

        session.completePaymentMethodSelection({
          newTotal: getPaymentTotal(),
          newLineItems: getLineItems(),
          newShippingMethods: getApplePayShippingMethods()
        })
      }
      catch {
        clearTemporaryStores()
        return session.completeMerchantValidation({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('unknown')]
        })
      }
    }

    // Session validation callback
    session.onvalidatemerchant = async () => {
      try {
        // add To cart with the PDP context passed through
        if (payload && storeContext) {
          await cart.add({
            productId: payload.productId,
            upc: payload.upc,
          }, {
            addToCartContextKey: storeContext,
            storeId: payload.storeId
          })
          await cart.getApplicableShippingMethods()

          if (payload.storeId) {
            await cart.addShippingAddress([{
              ...cart.shippingMethods[0].address,
              firstName: `${cart.shippingMethods[0].storeName}`,
              lastName: payload.storeId,
              shippingId: cart.shippingMethods[0].shippingId,
              storeId: payload.storeId
            }], recaptcha, 'APPLE_PAY_EXPRESS', 'cookie')
          }

          const promoCode = useSessionStorage('autoPromoCode', '')
          if (isAutoPromoCodeEnabled && promoCode.value) {
            try {
              await cart.applyCoupon(promoCode.value.trim())
            }
            catch {
              // Decision not to interrupt session when promotion is not applied in order to potentially save the sale
              log.error('Coupon could not be applied')
            }
            finally {
              promoCode.value = ''
            }
          }

          refreshPaymentRequestWithCurrentCartData()
        }
        const { data } = await api.cart.getPaymentSession({
          referer: location.host,
          paymentMethodId: 'DW_APPLE_PAY'
        }, {
          headers: {
            'content-type': 'application/json'
          }
        })
        // @ts-expect-error incorrect contract type
        const merchantSession = data.value?.meta?.merchantSession
        session.completeMerchantValidation(
          allowApplePayForAll ? JSON.parse(JSON.stringify(merchantSession)) : merchantSession
        )
      }
      catch (err) {
        log.error(err)
        clearTemporaryStores()
        return session.completeMerchantValidation({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('unknown')]
        })
      }
    }

    // Shipping Address change callback
    session.onshippingcontactselected = async (event) => {
      const countryCode = useCountryCode()
      // check if the country code of the selected shipping address is valid for the application locale
      if (event.shippingContact.countryCode !== countryCode) {
        clearTemporaryStores()
        return session.completeShippingContactSelection({
          newShippingMethods: [],
          newTotal: { label: $t.error, amount: '1', type: 'pending' },
          newLineItems: [],
          errors: [new ApplePayError('shippingContactInvalid', 'country', $t.applePayCountryError)]
        })
      }
      // check if the city name is longer than 50 characters. It would be stuck downstream so we need to throw a message here instead. https://digital.vfc.com/jira/browse/GLOBAL15-91044
      if (event.shippingContact.locality && event.shippingContact.locality.length > 50) {
        return session.completeShippingContactSelection({
          newShippingMethods: [],
          newTotal: { label: $t.error, amount: '1', type: 'pending' },
          newLineItems: [],
          errors: [new ApplePayError('shippingContactInvalid', 'locality', $t.applePayCityNameLength)]
        })
      }

      // Potentially Add address validation via "getProvinceList" call, once it is migrated in https://digital.vfc.com/jira/browse/GLOBAL15-48687
      try {
        const captchaResponse = enableShippingAddressRecaptcha ? await recaptcha.execute('login') : ''
        const { totals: newTotals } = await api.cart.$addShippingAddress(cart.basketId, {
          ...mapAddressData(event?.shippingContact)[0],
          subscriptions: {
            newsletterConsent: false
          },
          ...captchaResponse && { recaptcha_response: captchaResponse }
        }, { ...queryParams.value, context: 'APPLE_PAY_EXPRESS' })
        // update totals, because the tax amounts from SFCC are coming in this step
        cart.totals = newTotals
      }
      catch {
        clearTemporaryStores()

        if (ApplePaySession.supportsVersion(3)) {
          return session.completeShippingContactSelection({
            newShippingMethods: [],
            newTotal: { label: $t.error, amount: '1', type: 'pending' },
            newLineItems: [],
            errors: [new ApplePayError('addressUnserviceable', 'postalAddress', $t.applePayShippingMethodError)]
          })
        }
        else {
          return session.completeShippingContactSelection(
            ApplePaySession.STATUS_FAILURE,
            [],
            { label: $t.error, amount: '1', type: 'pending' },
            []
          )
        }
      }

      await cart.getApplicableShippingMethods()

      session.completeShippingContactSelection({
        newTotal: getPaymentTotal(),
        newLineItems: getLineItems(),
        newShippingMethods: getApplePayShippingMethods()
      })
    }

    // Shipping Method Selected callback
    session.onshippingmethodselected = async (event) => {
      const shippingMethod: ApplePayShippingMethod = event.shippingMethod
      await cart.getApplicableShippingMethods()

      // check if the selected shipping method is in the new list of available shipping methods upon address selection
      const isSelectedAvailable = cart.shippingMethods[0].applicableShippingMethods
        .some((method) => method.code === shippingMethod.identifier)
      if (!isSelectedAvailable) {
        clearTemporaryStores()
        return session.completeShippingMethodSelection({
          // see https://developer.apple.com/documentation/apple_pay_on_the_web/applepayshippingmethodupdate
          newShippingMethods: getApplePayShippingMethods(),
          newTotal: { label: $t.applePayShippingMethodListError, amount: '1', type: 'pending' },
          newLineItems: [],
          // @ts-expect-error incorrect applepayjs types for "ApplePayShippingMethodUpdate"
          errors: [new ApplePayError('shippingContactInvalid', 'country', $t.applePayCountryError)]
        })
      }
      try {
        const { totals: newTotals } = await api.cart.$setShippingMethod({
          cartId: cart.basketId,
          code: shippingMethod.identifier,
          shippingId: 'me',
          shopperAppliedSM: true
        }, { ...queryParams.value, context: isCollectionPoint.value ? 'COLLECTION' : 'APPLE_PAY_EXPRESS' }) as CartState
        // we need to update totals on changing shipping methods because when we are coming from PDP we can't run prepareOrder where this update would happen
        // the information given by BE team for the ticket GLOBAL15-58811
        if (storeContext === 'applePayPdp' && newTotals) cart.totals = newTotals
      }
      catch {
        clearTemporaryStores()

        if (ApplePaySession.supportsVersion(3)) {
          return session.completeShippingMethodSelection({
            newShippingMethods: [],
            newTotal: { label: $t.error, amount: '1', type: 'pending' },
            newLineItems: [],
            // @ts-expect-error incorrect applepayjs types for "ApplePayShippingMethodUpdate"
            errors: [new ApplePayError('addressUnserviceable', 'postalAddress', $t.applePayShippingMethodError)]
          })
        }
        else {
          return session.completeShippingMethodSelection(
            ApplePaySession.STATUS_FAILURE,
            { label: $t.applePayShippingMethodListError, amount: '1', type: 'pending' },
            []
          )
        }
      }
      if (storeContext !== 'applePayPdp') await checkout.prepareOrder()
      const update = {
        newTotal: getPaymentTotal(),
        newLineItems: getLineItems()
      }
      session.completeShippingMethodSelection(update)
    }

    // Payment authorization callback
    session.onpaymentauthorized = async (event) => {
      // Additionally to the validation provided in configuration, we need to check if:
      // 1. fields like AddressLine1, postalCode, name is not just a whitespace
      // 2. fields do not contain non-latin characters e.g. Cyrilic, Chinese, Japanese
      const [isBillingAddressValid, invalidBillingField] = isAddressValid(event?.payment.billingContact)
      if (!isBillingAddressValid) {
        return session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('billingContactInvalid', invalidBillingField)]
        })
      }

      const [isShippingAddressValid, invalidShippingField] = isAddressValid(event?.payment.shippingContact)
      if (!isShippingAddressValid) {
        return session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('shippingContactInvalid', invalidShippingField)]
        })
      }

      // if we have data in session storge around selected collection point and if the collection poing is currently active as method, build the object
      const collectionPointsData = (selectedCollectionPoint.value && isCollectionPoint.value)
        ? {
            storeId: selectedCollectionPoint.value.storeId,
            storeName: selectedCollectionPoint.value.storeName,
            optionType: selectedCollectionPoint.value.optionType,
            bookingCode: selectedCollectionPoint.value.bookingCode
          }
        : {}

      const address = mapAddressData(event?.payment.shippingContact)[0] as CartStoreAddress
      cart.shippingMethods[0].address = JSON.parse(JSON.stringify({
        id: null,
        stateCode: null,
        addressLine2: '',
        province: null,
        subscriptions: {
          newsletterConsent: false
        },
        ...address,
        phone: formatE164phone(address.phone, address.phoneCode),
        addressId: address.addressId
      })) as CartStoreAddress

      const getShippingId = (storeId: string) =>
        cart.shippingMethods.find((m) => m.storeId === storeId)?.shippingId

      try {
        const captchaResponse = enableShippingAddressRecaptcha ? await recaptcha.execute('login') : ''
        await api.cart.$addShippingAddress(cart.basketId, {
          ...mapAddressData(event?.payment.shippingContact)[0],
          subscriptions: {
            newsletterConsent: false
          },
          ...collectionPointsData,
          ...(storeContext === 'applePayPdp' && payload?.storeId && { storeId: payload.storeId, shippingId: getShippingId(payload.storeId) }),
          ...captchaResponse && { recaptcha_response: captchaResponse }
        }, { ...queryParams.value, context: isCollectionPoint.value && selectedCollectionPoint.value?.bookingCode ? 'COLLECTION' : 'APPLE_PAY_EXPRESS' })
      }
      catch (error: any) {
        log.error('Error adding shipping address', error)
      }

      let isPaymentInstrumentValid = false
      // Check the cart flash massages before making this call
      // Thicket that should define the UX https://digital.vfc.com/jira/browse/GLOBAL15-46952
      try {
        if (storeContext !== 'applePayPdp') await checkout.prepareOrder()
      }
      catch (err) {
        log.error(err)
        isApplePayActive.value = false
        clearTemporaryStores()
        return session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('unknown')]
        })
      }

      try {
        if (storeContext !== 'applePayPdp') {
          const paymentInstrumentsResponse: any = await cart.postPaymentInstruments('DW_APPLE_PAY')
          // Get payment instruments and check if Apple Pay is still a valid method
          isPaymentInstrumentValid = !!paymentInstrumentsResponse.payment_instruments?.find(
            (method) => method.payment_method_id === 'DW_APPLE_PAY'
          )
        }
        else {
          isPaymentInstrumentValid = true
        }

        /** Proceed with payment flow if a payment instrument has been validated */
        if (isPaymentInstrumentValid) {
          try {
            // NOTE: ApplePay's "billingContact" doesn't provide emailAddress or phoneNumber
            // so we need to pull it from the shippingContact
            const billingAddress = {
              ...mapAddressData({
                ...event?.payment?.billingContact,
                emailAddress: event?.payment?.shippingContact?.emailAddress,
                phoneNumber: event?.payment?.shippingContact?.phoneNumber
              })[0],
              ...invoiceAddressData,
              ...collectionPointsData
            }

            if (!countries.value.some(({ value }) => value === billingAddress.countryCode)) {
              isApplePayActive.value = false
              clearTemporaryStores()
              return session.completePayment({
                status: ApplePaySession.STATUS_FAILURE,
                errors: [new ApplePayError('billingContactInvalid')]
              })
            }

            if (invoiceAddress) await cart.addBillingAddress(billingAddress)
            const captchaResponse = await recaptcha.execute('placeOrder')
            const orderData = await api.cart.$placeOrder({
              recaptcha_response: captchaResponse,
              cartId: cart.basketId,
              billingAddress,
              paymentMethod: {
                code: 'DW_APPLE_PAY',
                additionalData: [{
                  sessionID: uuid(),
                  c_applePayToken: JSON.stringify({ payment: { token: event.payment.token } })
                }]
              }
            }, {
              bid: storeContext === 'applePayPdp' ? 'cookie' : undefined
            })
            // Complete the order. Store to the checkout store to use on order confirmation page, then reset cart
            session.completePayment({
              status: ApplePaySession.STATUS_SUCCESS
            })

            try {
              // prepare data for confirmation page
              checkout.success(orderData as Order)
              checkout.isExpressCheckout = true
            }
            finally {
              log.info(`order placed: ${orderData.orderNumber}`)
              isApplePayActive.value = false
              storeContext === 'applePayPdp'
                ? await navigateTo(`/order-confirmation?storeContext=${storeContext}`)
                : await navigateTo('/order-confirmation')
              clearTemporaryStores()
              cart.reset()
            }
          }
          catch {
            isApplePayActive.value = false
            clearTemporaryStores()
            return session.completePayment({
              status: ApplePaySession.STATUS_FAILURE,
              errors: [new ApplePayError('unknown')]
            })
          }
        }
        else {
          // AP Payment is not supported for some reason
          isApplePayActive.value = false
          session.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
            errors: [new ApplePayError('unknown')]
          })
          clearTemporaryStores()
        }
      }
      catch {
        // Something went wrong along the way (ex. network issue)
        isApplePayActive.value = false
        clearTemporaryStores()
        return session.completePayment({
          status: ApplePaySession.STATUS_FAILURE,
          errors: [new ApplePayError('unknown')]
        })
      }
    }

    session.oncancel = (event) => {
      isApplePayActive.value = false
      clearTemporaryStores()
      log.info(`Apple Pay session canceled: ${JSON.stringify(event)}`)
    }
    // Begin Apple Pay Session
    session.begin()
    return session
  }

  return {
    /** Initializes Apple Pay session with full lifecycle management - main entry point for Apple Pay integration */
    initApplePaySession,
    /** Reactive boolean indicating if Apple Pay session is active (use for button loading states) */
    isApplePayActive
  }
}
